import { useState } from 'react';
import { Modal } from '../Common';
import { MappingStep } from './MappingStep';
import { ValidationStep } from './ValidationStep';
import { ImportResultStep } from './ImportResultStep';
import type { CSVAnalyzeResponse, CSVValidateResponse } from '../../types';

interface CSVImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
  embedded?: boolean;
}

type WizardStep = 'mapping' | 'validation' | 'import';

export function CSVImportWizard({
  isOpen,
  onClose,
  onImportComplete,
  embedded = false,
}: CSVImportWizardProps) {
  const [step, setStep] = useState<WizardStep>('mapping');

  // Step 1: Mapping state
  const [analyzeResponse, setAnalyzeResponse] = useState<CSVAnalyzeResponse | null>(null);
  const [confirmedMapping, setConfirmedMapping] = useState<Record<string, string | null>>({});
  const [fixedValues, setFixedValues] = useState<Record<string, string>>({});

  // Step 2: Validation state
  const [validateResponse, setValidateResponse] = useState<CSVValidateResponse | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const handleClose = () => {
    // Reset state when closing
    setStep('mapping');
    setAnalyzeResponse(null);
    setConfirmedMapping({});
    setFixedValues({});
    setValidateResponse(null);
    setSelectedRows(new Set());
    onClose();
  };

  const handleMappingConfirm = () => {
    setStep('validation');
  };

  const handleValidationBack = () => {
    setValidateResponse(null);
    setSelectedRows(new Set());
    setStep('mapping');
  };

  const handleImport = () => {
    setStep('import');
  };

  const handleImportComplete = () => {
    if (onImportComplete) {
      onImportComplete();
    }
    handleClose();
  };

  const getStepTitle = () => {
    switch (step) {
      case 'mapping':
        return 'Import CSV - Step 1: Column Mapping';
      case 'validation':
        return 'Import CSV - Step 2: Review & Select';
      case 'import':
        return 'Import CSV - Step 3: Results';
    }
  };

  const content = (
    <>
      {step === 'mapping' && (
        <MappingStep
          onAnalyzeComplete={setAnalyzeResponse}
          analyzeResponse={analyzeResponse}
          confirmedMapping={confirmedMapping}
          onMappingChange={setConfirmedMapping}
          fixedValues={fixedValues}
          onFixedValuesChange={setFixedValues}
          onConfirm={handleMappingConfirm}
        />
      )}

      {step === 'validation' && analyzeResponse && (
        <ValidationStep
          sessionId={analyzeResponse.session_id}
          confirmedMapping={confirmedMapping}
          fixedValues={fixedValues}
          onValidationComplete={setValidateResponse}
          validateResponse={validateResponse}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          onImport={handleImport}
          onBack={handleValidationBack}
        />
      )}

      {step === 'import' && analyzeResponse && (
        <ImportResultStep
          sessionId={analyzeResponse.session_id}
          rowNumbersToImport={Array.from(selectedRows)}
          onComplete={handleImportComplete}
          embedded={embedded}
        />
      )}
    </>
  );

  // Embedded mode: render content directly without modal
  if (embedded) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex gap-2">
            {['mapping', 'validation', 'import'].map((s, i) => (
              <div
                key={s}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  step === s
                    ? 'bg-dark-accent text-white'
                    : i < ['mapping', 'validation', 'import'].indexOf(step)
                    ? 'bg-green-600 text-white'
                    : 'bg-dark-elevated text-dark-text-secondary'
                }`}
              >
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
                  {i + 1}
                </span>
                {s === 'mapping' ? 'Mapping' : s === 'validation' ? 'Review' : 'Results'}
              </div>
            ))}
          </div>
        </div>
        {content}
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getStepTitle()}
      size="6xl"
    >
      {content}
    </Modal>
  );
}
